import { createContext, useContext } from 'react';
import { InterpreterFrom } from 'xstate';
import { useActor, useInterpret } from '@xstate/react';
import { boughtItemsMachine } from './boughtItems';
const BoughtItemsStateMachineContext = createContext({
  /*
   * Type assertion is never great, but our options here are assertion
   * or maintaining a simply massive object as a default that matches the
   * types but will only ever exist in the application for the briefest of
   * moments. This seems like a decent exception to the rule.
   */
  boughtItemsService: {} as InterpreterFrom<typeof boughtItemsMachine>
});
interface BoughtItemsStateMachineProviderProps {
  children: React.ReactNode;
}

/**
 * Convenience component that initiates the boughtItems state machine and then
 * wraps its children in a context provider that contains the initialized
 * state machine service.
 *
 * > Using useInterpret returns a service, which is a static reference to
 * > the running machine which can be subscribed to. This value never
 * > changes, so we don't need to worry about wasted re-renders.
 *
 * sources:
 * - https://xstate.js.org/docs/recipes/react.html#global-state-react-context
 * - https://xstate.js.org/docs/packages/xstate-react/#api
 */
export const BoughtItemsStateMachineProvider = ({
  children
}: BoughtItemsStateMachineProviderProps) => {
  const boughtItemsService = useInterpret(boughtItemsMachine);
  return <BoughtItemsStateMachineContext.Provider value={{
    boughtItemsService
  }}>
      {children}
    </BoughtItemsStateMachineContext.Provider>;
};

/**
 * This is a custom hook that wraps up the verbosity of retrieving the
 * `state` object and `send` method from the boughtItems machine context
 */
export const useBoughtItemsMachine = () => {
  const {
    boughtItemsService
  } = useContext(BoughtItemsStateMachineContext);
  const [state] = useActor(boughtItemsService);
  return {
    state,
    /*
     * The docs for `useActor` demonstrate that you should be able to use
     * the same `[state, send]` tuple from `useMachine`, but for some reason
     * that instance of `send` was losing the context when sending the event.
     * For now the `send` method directly off of the service is doing the trick
     *
     * https://xstate.js.org/docs/recipes/react.html#global-state-react-context
     */
    send: boughtItemsService.send
  };
};